<script setup>
import { ref } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import DefaultLayout from '@/layouts/default.vue';
import DepartmentsTreeTable from '@/components/rbac/DepartmentsTreeTable.vue';
import DepartmentFormDialog from '@/components/rbac/DepartmentFormDialog.vue';
import PermissionsMatrixDialog from '@/components/rbac/PermissionsMatrixDialog.vue';
import { useRbacStore } from '@/stores/rbac.js';
import { useToast } from 'primevue';

const rbacStore = useRbacStore();
const toast = useToast();

const showAddDialog = ref(false);
const showEditDialog = ref(false);
const showPermissionsDialog = ref(false);
const selectedDepartment = ref(null);

async function handleCreate(departmentData) {
  try {
    await rbacStore.createDepartment(departmentData);
    showAddDialog.value = false;
    toast.add({
      severity: 'success',
      summary: 'Отдел создан',
      detail: 'Отдел успешно создан',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось создать отдел';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

async function handleUpdate(departmentData) {
  try {
    await rbacStore.updateDepartment(selectedDepartment.value.id, departmentData);
    showEditDialog.value = false;
    selectedDepartment.value = null;
    toast.add({
      severity: 'success',
      summary: 'Отдел обновлен',
      detail: 'Отдел успешно обновлен',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось обновить отдел';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

function handleEdit(department) {
  selectedDepartment.value = department;
  showEditDialog.value = true;
}

async function handleDelete(department) {
  try {
    await rbacStore.deleteDepartment(department.id);
    toast.add({
      severity: 'success',
      summary: 'Отдел удален',
      detail: 'Отдел успешно удален',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось удалить отдел';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

function handleConfigurePermissions(department) {
  selectedDepartment.value = department;
  showPermissionsDialog.value = true;
}

function handlePermissionsSaved() {
  toast.add({
    severity: 'success',
    summary: 'Права сохранены',
    detail: 'Права доступа успешно обновлены',
    life: 2500,
  });
  showPermissionsDialog.value = false;
  selectedDepartment.value = null;
}

function handleCloseEditDialog() {
  showEditDialog.value = false;
  selectedDepartment.value = null;
}

function handleClosePermissionsDialog() {
  showPermissionsDialog.value = false;
  selectedDepartment.value = null;
}
</script>

<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >
      <template #title>
        <div class="flex justify-end">
          <Button
            label="Добавить отдел"
            icon="pi pi-plus"
            severity="info"
            rounded
            @click="showAddDialog = true"
          />
        </div>
      </template>

      <template #content>
        <DepartmentsTreeTable
          @edit="handleEdit"
          @delete="handleDelete"
          @configure-permissions="handleConfigurePermissions"
        />
      </template>
    </Card>

    <!-- Диалог создания отдела -->
    <DepartmentFormDialog
      v-model:visible="showAddDialog"
      mode="create"
      @save="handleCreate"
    />

    <!-- Диалог редактирования отдела -->
    <DepartmentFormDialog
      v-model:visible="showEditDialog"
      mode="edit"
      :department="selectedDepartment"
      @save="handleUpdate"
      @close="handleCloseEditDialog"
    />

    <!-- Диалог настройки прав -->
    <PermissionsMatrixDialog
      v-model:visible="showPermissionsDialog"
      entity-type="department"
      :entity-id="selectedDepartment?.id"
      :entity-name="selectedDepartment?.name"
      @saved="handlePermissionsSaved"
      @close="handleClosePermissionsDialog"
    />
  </DefaultLayout>
</template>

<style scoped>
</style>
